/**
 * ChildPracticePlayer — kid-facing practice player for the public
 * `/handoff/:tokenId/practice` route.
 *
 * Renders one of the 5 archetype activities end-to-end:
 *   welcome → (pick_kid?) → home → playing → activity_done → set_done
 *
 * The actual archetype player UI is delegated to
 * `web/src/screens/student/archetypes/PracticeActivityRouter`,
 * which dispatches on `scoring_rubric.archetype` to one of:
 * picture_naming (shipped in #251), receptive_directions,
 * sentence_completion, conversation_turns, sequencing
 * (shipped in #252 / #PL-WEB-RUBRICS).
 *
 * No auth required — the token IS the auth. Resolved via the
 * `api-resolve-child-practice-link` Edge Function (verify_jwt=false,
 * the second public Edge Function in the platform after
 * api-resolve-parent-handoff-token).
 *
 * Privacy mechanism: parent picks a nickname ("Otter", "Robin"...)
 * on the welcome screen; we store a salted SHA-256 of
 * (tokenId || ':' || nickname) in localStorage as
 * `practice:{token_id}:nickname`. The server never sees the raw
 * nickname; it just receives the hash and recomputes it on every
 * attempt submission.
 *
 * Offline-safe: scored trials are batched into an IndexedDB queue
 * (web/src/screens/student/PracticeQueue.ts) and flushed on
 * (a) activity completion, (b) visibilitychange → hidden,
 * (c) every 30s if non-empty, (d) online event.
 *
 * Out of scope (per the issue body):
 *   - clinician + parent + messaging UIs.
 *   - server-side hash verification (lives in api-record-practice-attempts).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  computeChildIdentifierHash,
  nicknameStorageKey,
  suggestNickname,
  FRIENDLY_ANIMAL_NICKNAMES,
} from '../../lib/practice/childIdentifier';
import {
  incrementRetryCount,
  readRetryCount,
} from '../../lib/practice/retryCounts';
import {
  QUEUE_BACKPRESSURE_THRESHOLD,
  enqueueAttempt,
  flushQueue,
  installPracticeQueueListeners,
  queueDepth,
  updateSessionSummary,
  type FlushPayload,
  type QueuedAttempt,
  type SessionSummary,
} from './PracticeQueue';
import PracticeActivityRouter from './archetypes/PracticeActivityRouter';
import type { PracticeActivityItemLike } from './archetypes/shared';
import { CheckIcon } from '../../components/icons/CheckIcon';
import { SparkleIcon } from '../../components/icons/SparkleIcon';

interface ExistingSummary {
  last_attempted_at: string | null;
  total_trials_attempted: number;
  total_trials_correct: number;
  activities_completed: number;
  per_activity: Array<{
    activity_payload_key: string;
    trials_attempted: number;
    trials_correct: number;
  }>;
}

interface PracticeSetItem {
  id: string;
  position: number;
  target_trials: number;
  cueing_level_override: unknown;
  activity_payload: Record<string, unknown> | null;
  scoring_rubric: Record<string, unknown> | null;
}

interface ResolveSuccess {
  practice_set_payload: {
    id: string;
    nickname: string | null;
    starts_at: string | null;
    ends_at: string | null;
    daily_attempt_cap: number;
    student_first_name: string;
    student_age_years: number | null;
    is_within_window: boolean;
    items: PracticeSetItem[];
  };
  existing_summary: ExistingSummary | null;
}

type ResolveError =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'consumed'
  | 'no_practice_set'
  | 'missing_token'
  | 'network'
  | 'crypto_unavailable'
  | 'unknown';

type Status =
  | { kind: 'resolving' }
  | { kind: 'resolved'; data: ResolveSuccess; childIdentifierHash: string }
  | { kind: 'error'; code: ResolveError };

type Phase =
  | 'welcome'
  | 'pick_kid'
  | 'home'
  | 'playing'
  | 'activity_done'
  | 'set_done'
  | 'error';

interface Props {
  tokenId: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

/** Picture-naming trial — defined in the picture_naming renderer
 *  (`web/src/screens/student/archetypes/PictureNaming.tsx`). The
 *  router still uses this shape when it synthesizes a default
 *  picture-naming trial from a missing payload. */

const HUMAN_ERROR: Record<ResolveError, { title: string; body: string }> = {
  not_found: {
    title: 'Practice link not found',
    body: 'This link doesn\u2019t exist. Ask your parent to send a new one.',
  },
  expired: {
    title: 'Practice link expired',
    body: 'This practice set has closed. Ask your SLP for a new one.',
  },
  revoked: {
    title: 'Practice link was revoked',
    body: 'Your SLP ended this link. Ask them for a new one.',
  },
  consumed: {
    title: 'Practice link is full',
    body: 'This link has been opened the maximum number of times. Ask your SLP for a new one.',
  },
  no_practice_set: {
    title: 'No practice set yet',
    body: 'Your SLP hasn\u2019t added any practice items to this link yet.',
  },
  missing_token: {
    title: 'Invalid link',
    body: 'The URL looks incomplete. Make sure you copied the whole link.',
  },
  network: {
    title: 'Could not reach the server',
    body: 'Check your internet connection. We saved your work — try again in a moment.',
  },
  crypto_unavailable: {
    title: 'This browser can\u2019t run practice',
    body: 'We need a secure connection (HTTPS) or a newer browser to keep your work safe. Ask your SLP for help opening this link.',
  },
  unknown: {
    title: 'Something went wrong',
    body: 'Try again in a moment. If the problem persists, ask your SLP.',
  },
};

/** Max retries per activity per day per child_identifier_hash (AC #7). */
const MAX_DAILY_RETRIES_PER_ACTIVITY = 2;

/**
 * Self-correction windows are now per-archetype (defined in the
 * rubric validators in `web/src/lib/practice/rubrics.ts`). The
 * picture-naming renderer keeps the 3s window from #251 for
 * backward compatibility; the 4 new archetypes default to 4s.
 */

function safeUuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — Math.random is fine for a client id.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Stable key for "which kid has practiced on this token" tracking. */
function kidsIndexKey(tokenId: string): string {
  return `practice:${tokenId}:kids_index`;
}

function readKidsIndex(tokenId: string): Array<{
  hash: string;
  nickname: string;
  last_seen: string;
}> {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(kidsIndexKey(tokenId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry): Array<{
      hash: string;
      nickname: string;
      last_seen: string;
    }> => {
      if (
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { hash?: unknown }).hash === 'string' &&
        typeof (entry as { nickname?: unknown }).nickname === 'string' &&
        typeof (entry as { last_seen?: unknown }).last_seen === 'string'
      ) {
        return [{
          hash: (entry as { hash: string }).hash,
          nickname: (entry as { nickname: string }).nickname,
          last_seen: (entry as { last_seen: string }).last_seen,
        }];
      }
      return [];
    });
  } catch {
    return [];
  }
}

function writeKidsIndex(
  tokenId: string,
  index: ReadonlyArray<{ hash: string; nickname: string; last_seen: string }>,
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(kidsIndexKey(tokenId), JSON.stringify(index));
  } catch {
    // localStorage quota — fail silently; pick_kid screen is best-effort.
  }
}

/**
 * Refresh the active kid's `last_seen` in the kids index so the
 * pick_kid screen's "last seen X ago" stays accurate across visits.
 * No-op if the hash isn't already in the index (e.g. first visit
 * via the welcome screen, which already stamps `last_seen` on
 * insert).
 */
function bumpLastSeen(tokenId: string, hash: string): void {
  const index = readKidsIndex(tokenId);
  const idx = index.findIndex((e) => e.hash === hash);
  if (idx < 0) return;
  const now = new Date().toISOString();
  const existing = index[idx];
  if (!existing) return;
  const next = index.slice();
  next[idx] = { hash: existing.hash, nickname: existing.nickname, last_seen: now };
  writeKidsIndex(tokenId, next);
}

/** Storage key for the per-hash picker-skip preference set by
 *  the "Stay as {nickname}" button on the pick_kid screen. Keyed
 *  by hash so other kids on the same device still see the picker. */
function skipPickerKey(tokenId: string, hash: string): string {
  return `practice:${tokenId}:skip_picker:${hash}`;
}

function readSkipPicker(tokenId: string, hash: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(skipPickerKey(tokenId, hash)) === '1';
  } catch {
    return false;
  }
}

function writeSkipPicker(tokenId: string, hash: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(skipPickerKey(tokenId, hash), '1');
  } catch {
    // localStorage quota — fail silently; pick_kid screen is best-effort.
  }
}

/** Clear the per-hash "Stay as {nickname}" skip flag so the
 *  picker reappears on the next mount. Wired to a "Not {nickname}?"
 *  link on the home screen (#349) so a parent on a shared device
 *  can undo the choice without dropping into localStorage. */
function clearSkipPicker(tokenId: string, hash: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(skipPickerKey(tokenId, hash));
  } catch {
    // localStorage access failure — fail silently; the picker
    // effect will fall back to its index-length heuristic.
  }
}

function truncateHash(hash: string): string {
  return `${hash.slice(0, 6)}\u2026`;
}

function relativeDate(iso: string | null): string {
  if (!iso) return 'first time';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 24 * 60 * 60_000) return `${Math.floor(ms / (60 * 60_000))} h ago`;
  const days = Math.floor(ms / (24 * 60 * 60_000));
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

function newSessionSummary(): SessionSummary {
  return {
    id: safeUuid(),
    started_at: new Date().toISOString(),
    ended_at: new Date().toISOString(),
    trials_attempted: 0,
    trials_correct: 0,
    activities_completed: 0,
    bail_trial_index: null,
    device_fingerprint_hash: 'web-v1',
  };
}

/**
 * Build the picture_naming player. Extracted as a named export so
 * tests can render the home/pick_kid branches directly without
 * driving the parent `useEffect` fetch (mirrors the pattern
 * HandoffViewer uses for its resolved-state test).
 */
export function ChildPracticePlayerResolved({
  data,
  tokenId,
  supabaseUrl,
  supabaseAnonKey,
  childIdentifierHash,
  initialNickname,
}: {
  data: ResolveSuccess;
  tokenId: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  childIdentifierHash: string;
  initialNickname: string;
}): ReactElement {
  const practiceSet = data.practice_set_payload;
  const [phase, setPhase] = useState<Phase>('home');
  const [nickname, setNickname] = useState(initialNickname);
  const [activeHash, setActiveHash] = useState(childIdentifierHash);
  const [currentItemIdx, setCurrentItemIdx] = useState(0);
  const [trialIdx, setTrialIdx] = useState(0);
  const [score, setScore] = useState({ attempted: 0, correct: 0 });
  const [sessionAttempts, setSessionAttempts] = useState(0);
  const [queueDepthNow, setQueueDepthNow] = useState(0);

  // Stable per-play-session identifier. Every attempt in this session
  // shares this id so the server can group trials into one session
  // summary and so multiple practice links on the same device don't
  // collapse into a single bucket (was hardcoded to 'pending').
  const sessionLocalIdRef = useRef(safeUuid());
  const sessionStartedAtRef = useRef(new Date().toISOString());
  const sessionSummaryRef = useRef<SessionSummary>({
    ...newSessionSummary(),
    id: sessionLocalIdRef.current,
    started_at: sessionStartedAtRef.current,
  });

  const endpointUrl = `${supabaseUrl}/functions/v1/api-record-practice-attempts`;
  const queueOpts = useMemo(() => ({ anonKey: supabaseAnonKey }), [
    supabaseAnonKey,
  ]);

  // Install auto-flush listeners on mount; tear down on unmount.
  useEffect(() => {
    const dispose = installPracticeQueueListeners(endpointUrl, queueOpts);
    return dispose;
  }, [endpointUrl, queueOpts]);

  // Poll queue depth so the AC #12 "Saving your work…" surface
  // reflects the latest count.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = window.setInterval(() => {
      void queueDepth().then((n) => setQueueDepthNow(n));
    }, 5_000);
    void queueDepth().then((n) => setQueueDepthNow(n));
    return (): void => window.clearInterval(id);
  }, []);

  const queueOverloaded = queueDepthNow > QUEUE_BACKPRESSURE_THRESHOLD;

  // Pick-kid detection: if there are >1 distinct stored hashes for
  // this token AND we haven't picked yet, force the picker first.
  // The single-resolve endpoint doesn't return all sibling hashes,
  // so the picker is client-side state; matches AC #4's intent.
  // The skip flag is set by the "Stay as {nickname}" button on the
  // picker so re-visits by the same kid don't bounce through it.
  useEffect(() => {
    if (readSkipPicker(tokenId, childIdentifierHash)) return;
    const index = readKidsIndex(tokenId);
    const others = index.filter((e) => e.hash !== childIdentifierHash);
    if (others.length > 0) {
      setPhase('pick_kid');
    }
  }, [tokenId, childIdentifierHash]);

  // Refresh this kid's last_seen so the pick_kid screen's
  // "last seen X ago" stays accurate across visits. Welcome-screen
  // submit already stamps last_seen on first insert; this handles
  // the return-visit path (#293 nit).
  useEffect(() => {
    bumpLastSeen(tokenId, childIdentifierHash);
  }, [tokenId, childIdentifierHash]);

  function startActivity(idx: number): void {
    setCurrentItemIdx(idx);
    setTrialIdx(0);
    setScore({ attempted: 0, correct: 0 });
    setSessionAttempts(0);
    setPhase('playing');
  }

  function switchToHash(hash: string, newNickname: string): void {
    setActiveHash(hash);
    setNickname(newNickname);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(nicknameStorageKey(tokenId), newNickname);
    }
    setPhase('home');
  }

  async function recordTrial(opts: {
    item: PracticeSetItem;
    trialIndex: number;
    isCorrect: boolean;
    latencyMs: number;
    selfCorrected: boolean;
    usedCue: boolean;
    expected: unknown;
    response: unknown;
    promptKind: 'picture' | 'audio' | 'text' | 'mixed';
  }): Promise<void> {
    const prevSummary = sessionSummaryRef.current;
    const nextSummary: SessionSummary = {
      ...prevSummary,
      ended_at: new Date().toISOString(),
      trials_attempted: prevSummary.trials_attempted + 1,
      trials_correct: prevSummary.trials_correct + (opts.isCorrect ? 1 : 0),
    };
    sessionSummaryRef.current = nextSummary;

    const attempt: QueuedAttempt = {
      id: safeUuid(),
      practice_set_item_id: opts.item.id,
      session_local_id: sessionLocalIdRef.current,
      attempted_at: new Date().toISOString(),
      trial_index: opts.trialIndex,
      prompt_kind: opts.promptKind,
      expected_response: opts.expected,
      child_response: opts.response,
      is_correct: opts.isCorrect,
      response_latency_ms: opts.latencyMs,
      self_corrected: opts.selfCorrected,
      used_cue: opts.usedCue,
      client_metadata: { ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' },
    };
    const payload: FlushPayload = {
      token: tokenId,
      child_identifier_hash: activeHash,
      nickname,
      attempts: [attempt],
      session: nextSummary,
    };
    try {
      await enqueueAttempt(attempt.id, payload);
      setQueueDepthNow((n) => n + 1);
    } catch {
      // localStorage quota or other write failure — surface in UI is
      // already covered by the "Saving your work…" gate above.
    }
  }

  async function flushAndMaybeLeaveActivity(opts: {
    finished: boolean;
  }): Promise<void> {
    if (opts.finished) {
      await flushQueue(endpointUrl, queueOpts);
    }
  }

  async function onActivityDone(): Promise<void> {
    const updatedSummary: SessionSummary = {
      ...sessionSummaryRef.current,
      activities_completed: sessionSummaryRef.current.activities_completed + 1,
      ended_at: new Date().toISOString(),
    };
    sessionSummaryRef.current = updatedSummary;
    await updateSessionSummary(sessionLocalIdRef.current, updatedSummary);
    void flushAndMaybeLeaveActivity({ finished: true });
    const allDone =
      currentItemIdx + 1 >= practiceSet.items.length ||
      sessionAttempts + 1 >= (practiceSet.daily_attempt_cap ?? 5);
    if (allDone) {
      setPhase('set_done');
    } else {
      setPhase('activity_done');
    }
  }

  if (phase === 'pick_kid') {
    const index = readKidsIndex(tokenId).filter(
      (e) => e.hash !== childIdentifierHash,
    );
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Who&rsquo;s practicing?</h1>
            <p className="tagline">Pick a kid to switch.</p>
          </header>
        </div>
        <ul className="kid-list">
          {index.map((entry) => (
            <li key={entry.hash}>
              <button
                type="button"
                className="snap-button"
                onClick={(): void =>
                  switchToHash(entry.hash, entry.nickname)}
              >
                <strong>{truncateHash(entry.hash)}</strong>{' '}
                <span className="tagline">
                  last seen {relativeDate(entry.last_seen)}
                </span>
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="link-button"
              onClick={(): void => {
                // Persist "Stay as {nickname}" so re-visits by this
                // kid skip the picker (#293 nit). Per-hash key so
                // siblings on the same device still see it.
                writeSkipPicker(tokenId, childIdentifierHash);
                setPhase('home');
              }}
            >
              ← Stay as {nickname}
            </button>
          </li>
        </ul>
      </main>
    );
  }

  if (phase === 'home') {
    const totalCorrect = data.existing_summary?.total_trials_correct ?? 0;
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Hi, {nickname}!</h1>
            <p className="tagline">
              {practiceSet.student_first_name}&rsquo;s practice is ready.
            </p>
          </header>
        </div>
        {totalCorrect > 0 ? (
          <p className="auth-confirm" role="status">
            <CheckIcon size={16} aria-hidden="true" /> {totalCorrect} correct so far
          </p>
        ) : null}
        {queueOverloaded ? (
          <p className="error" role="alert">
            Saving your work… please wait until the queue catches up.
          </p>
        ) : null}
        {practiceSet.items.length === 0 ? (
          <p className="tagline">
            No practice items yet. Check back later!
          </p>
        ) : null}
        {/* #349: surface the picker even after the parent picked
            "Stay as {nickname}" once. Clearing the per-hash skip flag
            here makes the next mount (and any same-tab revisit) land
            on pick_kid again. Stays reachable even on a solo-kid
            device so the parent can re-arm sibling switching. */}
        <button
          type="button"
          className="link-button"
          onClick={(): void => {
            clearSkipPicker(tokenId, childIdentifierHash);
            setPhase('pick_kid');
          }}
        >
          Not {nickname}?
        </button>
        {practiceSet.items.length === 0 ? null : (
          <ul className="activity-tiles">
            {practiceSet.items.map((item, idx) => {
              const key = item.activity_payload &&
                typeof (item.activity_payload as { key?: unknown }).key === 'string'
                ? (item.activity_payload as { key: string }).key
                : `item-${item.id}`;
              const summary = data.existing_summary?.per_activity.find(
                (a) => a.activity_payload_key === key,
              );
              const best = summary && summary.trials_attempted > 0
                ? `${summary.trials_correct}/${summary.trials_attempted}`
                : null;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className="snap-button"
                    onClick={(): void => startActivity(idx)}
                    disabled={queueOverloaded}
                  >
                    <strong>Activity {idx + 1}</strong>
                    <span className="tagline">
                      Picture naming · {item.target_trials} tries
                    </span>
                    {best ? (
                      <span className="tagline">Best: {best}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    );
  }

  if (phase === 'playing') {
    const item = practiceSet.items[currentItemIdx];
    if (!item) {
      setPhase('set_done');
      return <main className="screen handoff" />;
    }
    const targetTrials = item.target_trials || 10;
    const routerItem: PracticeActivityItemLike = {
      id: item.id,
      scoring_rubric: item.scoring_rubric,
      activity_payload: item.activity_payload,
      target_trials: targetTrials,
    };
    // Advance the trial index when the renderer finishes its
    // single presentation. The router runs one rubric = one
    // trial (with internal mini-steps inside).
    return (
      <PracticeActivityRouter
        item={routerItem}
        trialIndex={trialIdx}
        totalTrials={targetTrials}
        onBackToActivities={(): void => {
          setCurrentItemIdx((i) => i + 1);
          setTrialIdx(0);
          setScore({ attempted: 0, correct: 0 });
          setPhase('home');
        }}
        onTrialComplete={async (outcome): Promise<void> => {
          setScore((s) => ({
            attempted: s.attempted + 1,
            correct: s.correct + (outcome.isCorrect ? 1 : 0),
          }));
          setSessionAttempts((n) => n + 1);
          await recordTrial({
            item,
            trialIndex: trialIdx,
            isCorrect: outcome.isCorrect,
            latencyMs: outcome.latencyMs,
            selfCorrected: outcome.selfCorrected,
            usedCue: outcome.usedCue,
            expected: outcome.expected,
            response: outcome.response,
            promptKind: outcome.promptKind,
          });
          if (trialIdx + 1 >= targetTrials) {
            await onActivityDone();
          } else {
            setTrialIdx((i) => i + 1);
          }
        }}
      />
    );
  }

  if (phase === 'activity_done') {
    const item = practiceSet.items[currentItemIdx];
    const persistedRetries = item
      ? readRetryCount(tokenId, activeHash, item.id)
      : 0;
    const canRetry =
      persistedRetries < MAX_DAILY_RETRIES_PER_ACTIVITY &&
      sessionAttempts < (practiceSet.daily_attempt_cap ?? 5);
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Great job!</h1>
            <p className="tagline">
              You got {score.correct} of {score.attempted}.
            </p>
          </header>
        </div>
        <div className="celebration" aria-hidden="true">
          <SparkleIcon size={20} aria-hidden="true" />
          <SparkleIcon size={24} aria-hidden="true" />
          <SparkleIcon size={20} aria-hidden="true" />
        </div>
        {canRetry ? (
          <button
            type="button"
            className="snap-button"
            onClick={(): void => {
              if (item) {
                incrementRetryCount(tokenId, activeHash, item.id);
              }
              setTrialIdx(0);
              setScore({ attempted: 0, correct: 0 });
              setPhase('playing');
            }}
          >
            Try again?
          </button>
        ) : null}
        <button
          type="button"
          className="snap-button"
          onClick={(): void => {
            setCurrentItemIdx((i) => i + 1);
            setTrialIdx(0);
            setScore({ attempted: 0, correct: 0 });
            setPhase('home');
          }}
        >
          Back to activities
        </button>
      </main>
    );
  }

  if (phase === 'set_done') {
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Great work!</h1>
            <p className="tagline">
              You finished {Math.min(currentItemIdx + 1, practiceSet.items.length)} activities
              today, {nickname}.
            </p>
          </header>
        </div>
        <p className="tagline">See you next time!</p>
      </main>
    );
  }

  // 'welcome' and 'error' aren't reached from the resolved
  // component — they're handled in the parent <ChildPracticePlayer>.
  return (
    <main className="screen handoff">
      <p className="tagline">{loadingText(phase)}</p>
    </main>
  );
}

function loadingText(phase: Phase): string {
  switch (phase) {
    case 'welcome':
      return 'Welcome!';
    case 'pick_kid':
      return 'Pick a kid…';
    case 'home':
      return 'Home';
    case 'playing':
      return 'Playing…';
    case 'activity_done':
      return 'All done!';
    case 'set_done':
      return 'Great work!';
    case 'error':
      return 'Error';
  }
}

/**
 * `PlayingPhase` was the inline picture_naming player in #251.
 * Moved to `web/src/screens/student/archetypes/PictureNaming.tsx`
 * so the dispatcher can treat it like every other archetype.
 * The old component was removed from this file in #252.
 */

export function ChildPracticePlayer({
  tokenId,
  supabaseUrl,
  supabaseAnonKey,
}: Props): ReactElement {
  const [status, setStatus] = useState<Status>({ kind: 'resolving' });
  const [nickname, setNickname] = useState<string>(() => {
    if (typeof localStorage === 'undefined') return suggestNickname();
    return (
      localStorage.getItem(nicknameStorageKey(tokenId)) ?? suggestNickname()
    );
  });
  const [computedHash, setComputedHash] = useState<string | null>(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    if (!tokenId) {
      setStatus({ kind: 'error', code: 'missing_token' });
      return;
    }
    // Feature-detect Web Crypto up front: `crypto.subtle` is only
    // exposed in a secure context (HTTPS / localhost) and on
    // browsers that support SubtleCrypto. Without it we cannot
    // compute the per-kid `child_identifier_hash`, so every flush
    // would POST an empty hash and the server would reject every
    // attempt with 403 hash_mismatch — leaving the kid stuck on
    // the AC #12 "Saving your work…" backpressure surface with no
    // explanation. Surface a dedicated error state instead (#296).
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      setStatus({ kind: 'error', code: 'crypto_unavailable' });
      return;
    }
    let cancelled = false;
    void (async () => {
      // Read existing nickname (if any) and pre-compute the hash so
      // the resolve call can include it for existing_summary.
      const stored =
        (typeof localStorage !== 'undefined' &&
          localStorage.getItem(nicknameStorageKey(tokenId))) ||
        null;
      let hash: string | null = null;
      if (stored) {
        try {
          hash = await computeChildIdentifierHash(tokenId, stored);
          if (!cancelled) {
            setComputedHash(hash);
            setNickname(stored);
          }
        } catch {
          hash = null;
        }
      }

      const qs = hash
        ? `?token=${encodeURIComponent(tokenId)}&child_identifier_hash=${encodeURIComponent(hash)}`
        : `?token=${encodeURIComponent(tokenId)}`;
      const url = `${supabaseUrl}/functions/v1/api-resolve-child-practice-link${qs}`;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            apikey: supabaseAnonKey,
            authorization: `Bearer ${supabaseAnonKey}`,
          },
        });
        if (cancelled) return;
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({
            error: 'unknown',
          }))) as { error?: string };
          const code = (
            [
              'not_found',
              'expired',
              'revoked',
              'consumed',
              'no_practice_set',
            ] as ResolveError[]
          ).includes(errBody.error as ResolveError)
            ? (errBody.error as ResolveError)
            : 'unknown';
          setStatus({ kind: 'error', code });
          return;
        }
        const data = (await res.json()) as ResolveSuccess;
        if (cancelled) return;
        if (!stored) {
          // First visit: we have a stored? No — first visit means no
          // stored nickname. We need to enter the welcome phase
          // BEFORE resolving existing_summary. Resolve again with
          // the chosen nickname later. For now, store the payload
          // with no hash and let the welcome screen prompt.
          setStatus({ kind: 'resolved', data, childIdentifierHash: '' });
        } else {
          setStatus({
            kind: 'resolved',
            data,
            childIdentifierHash: hash ?? '',
          });
        }
      } catch {
        if (!cancelled) setStatus({ kind: 'error', code: 'network' });
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [tokenId, supabaseUrl, supabaseAnonKey]);

  if (status.kind === 'resolving') {
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Loading…</h1>
            <p className="tagline">Getting your practice set ready.</p>
          </header>
        </div>
      </main>
    );
  }

  if (status.kind === 'error') {
    const human = HUMAN_ERROR[status.code];
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>{human.title}</h1>
          </header>
        </div>
        <p>{human.body}</p>
      </main>
    );
  }

  // Welcome phase: render the welcome screen over a resolved
  // payload so we don't re-fetch after nickname submit.
  if (
    computedHash === null &&
    typeof localStorage !== 'undefined' &&
    !localStorage.getItem(nicknameStorageKey(tokenId))
  ) {
    return (
      <WelcomeScreen
        studentFirstName={status.data.practice_set_payload.student_first_name}
        defaultNickname={nickname}
        submitting={computing}
        onSubmit={(name): void => {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(nicknameStorageKey(tokenId), name);
          }
          setComputing(true);
          void (async (): Promise<void> => {
            try {
              const hash = await computeChildIdentifierHash(tokenId, name);
              // Update kids index.
              const index = readKidsIndex(tokenId);
              const filtered = index.filter((e) => e.hash !== hash);
              filtered.unshift({
                hash,
                nickname: name,
                last_seen: new Date().toISOString(),
              });
              writeKidsIndex(tokenId, filtered.slice(0, 8));
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem(nicknameStorageKey(tokenId), name);
              }
              setComputedHash(hash);
              setNickname(name);
              setStatus({
                kind: 'resolved',
                data: status.data,
                childIdentifierHash: hash,
              });
            } catch {
              setStatus({ kind: 'error', code: 'unknown' });
            } finally {
              setComputing(false);
            }
          })();
        }}
      />
    );
  }

  return (
    <ChildPracticePlayerResolved
      data={status.data}
      tokenId={tokenId}
      supabaseUrl={supabaseUrl}
      supabaseAnonKey={supabaseAnonKey}
      childIdentifierHash={status.childIdentifierHash || computedHash || ''}
      initialNickname={nickname}
    />
  );
}

function WelcomeScreen({
  studentFirstName,
  defaultNickname,
  submitting,
  onSubmit,
}: {
  studentFirstName: string;
  defaultNickname: string;
  submitting: boolean;
  onSubmit: (name: string) => void;
}): ReactElement {
  const [name, setName] = useState(defaultNickname);
  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Hi {studentFirstName}!</h1>
          <p className="tagline">
            Pick a nickname to start practicing.
          </p>
        </header>
      </div>
      <label className="nickname-label">
        <span className="tagline">Your nickname</span>
        <input
          type="text"
          value={name}
          onChange={(e): void => setName(e.currentTarget.value)}
          placeholder="Otter, Robin, Panda…"
          maxLength={32}
          className="nickname-input"
        />
      </label>
      <div className="suggestion-row">
        {FRIENDLY_ANIMAL_NICKNAMES.map((animal) => (
          <button
            type="button"
            key={animal}
            className="link-button"
            onClick={(): void => setName(animal)}
          >
            {animal}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="snap-button"
        disabled={submitting || name.trim().length === 0}
        onClick={(): void => onSubmit(name.trim())}
      >
        {submitting ? 'Loading…' : 'Start practicing'}
      </button>
    </main>
  );
}